// @flow

import { type FeatureName, type Feature } from '@tectonics/prs/feature';
import { type PaymentMethodCode } from '@tectonics/prs/payment-method';
import { type Trait } from '@tectonics/prs/trait';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';

import { type LookAndFeel } from '@tectonics/look-and-feel';


export type LookAndFeelAnalysis = {|
  +lookAndFeel: LookAndFeel,
  +unsupportedPaymentMethods: ImmutableArray<PaymentMethodCode>,
  +unsupportedFeatures: ImmutableArray<FeatureName>,
  +unsupportedTraits: ImmutableArray<[Trait, FeatureName]>,
|};

export function analyzeLookAndFeelAgainstSpec(lookAndFeel: LookAndFeel, spec: PaymentSpec): LookAndFeelAnalysis {
  return {
    lookAndFeel,
    unsupportedPaymentMethods: getUnsupportedPaymentMethods(lookAndFeel, spec),
    unsupportedFeatures: getUnsupportedFeatures(lookAndFeel, spec),
    unsupportedTraits: getUnsupportedTraits(lookAndFeel, spec),
  };
}

function getUnsupportedPaymentMethods(laf: LookAndFeel, spec: PaymentSpec): ImmutableArray<PaymentMethodCode> {
  const country = spec.country;
  const integrationsAsArray = Object.values(laf.prsSupport.integrations[country] || []);

  return spec.paymentMethods
    .map(({ code }) => code)
    .filter((code) => !integrationsAsArray.includes(code));
}

function getUnsupportedFeatures(laf: LookAndFeel, spec: PaymentSpec): ImmutableArray<FeatureName> {
  const paymentMethodsFeatures = spec.paymentMethods
    .reduce((carry, { features }) => [...carry, ...features], []);

  const allFeatures = [
    ...spec.features,
    ...paymentMethodsFeatures,
  ];

  return allFeatures
    .filter(feature => !doesLookAndFeelSupportFeature(laf, feature))
    .map(({ name }) => name);
}

function getUnsupportedTraits(laf: LookAndFeel, spec: PaymentSpec): ImmutableArray<[Trait, FeatureName]> {
  const paymentMethodsFeatures = spec.paymentMethods
    .reduce((carry, { features }) => [...carry, ...features], []);

  const allFeatures = [
    ...spec.features,
    ...paymentMethodsFeatures,
  ];

  const unsupportedTraitsReferences: Array<[Trait, FeatureName]> = [];

  allFeatures.forEach(feature => {
    const traits = feature.traits ? feature.traits : [];

    if (!doesLookAndFeelSupportFeature(laf, feature)) {
      const allTraitsReferences = traits.map(trait => [ trait, feature.name ]);

      unsupportedTraitsReferences.push(...allTraitsReferences);
    } else {
      const supportedFeature = getLookAndFeelSupportedFeatureByName(laf, feature.name);
      const supportedTraits = supportedFeature && supportedFeature.traits
        ? supportedFeature.traits
        : [];

      const featureUnsupportedTraitsReferences = traits
        .filter(trait => !supportedTraits.includes(trait))
        .map(trait => [ trait, feature.name ]);

      unsupportedTraitsReferences.push(...featureUnsupportedTraitsReferences);
    }
  });

  return unsupportedTraitsReferences;
}

function doesLookAndFeelSupportFeature(laf: LookAndFeel, feature: Feature): boolean {
  const lafImplementedFeaturesNames = laf.prsSupport.features.map(({ name }) => name);
  return lafImplementedFeaturesNames.includes(feature.name);
}

function getLookAndFeelSupportedFeatureByName(laf: LookAndFeel, name: string): ?Feature {
  return laf.prsSupport.features.find(feature => feature.name === name);
}
